import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useServiceDetail } from '../hooks/useApi';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : '';

function ServiceDetail() {
  const { t, getField } = useLanguage();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  // Try to get service from route state (passed from ServicesSection)
  const passedService = location.state?.service;

  // Fetch full detail from API
  const { data: apiData, loading, error } = useServiceDetail(slug);
  const detail = apiData?.data;

  // Merge: API data takes priority, fall back to route state
  const service = detail || passedService;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Lightbox keyboard navigation
  const gallery = detail?.gallery || [];

  const handleKeyDown = useCallback((e) => {
    if (!lightbox.open) return;
    if (e.key === 'Escape') setLightbox({ open: false, index: 0 });
    if (e.key === 'ArrowRight') setLightbox(prev => ({ ...prev, index: (prev.index + 1) % gallery.length }));
    if (e.key === 'ArrowLeft') setLightbox(prev => ({ ...prev, index: (prev.index - 1 + gallery.length) % gallery.length }));
  }, [lightbox.open, gallery.length]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightbox.open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox.open]);

  // Loading state
  if (loading && !service) {
    return (
      <div className="sd-loading">
        <div className="spinner" />
        <p>{t('sd.loading')}</p>
      </div>
    );
  }

  // Not found
  if (!service && !loading) {
    return (
      <div className="sd-not-found">
        <h2>{t('sd.notFound')}</h2>
        <p>{error || t('sd.notFoundDesc')}</p>
        <button className="btn btn--primary" onClick={() => navigate('/')}>
          {t('sd.backHome')}
        </button>
      </div>
    );
  }

  const videoUrl = detail?.video || service?.videoUrl || '';
  const title = getField(detail || service, 'title') || service?.name || '';
  const description = getField(detail || service, 'description') || '';
  const galleryLabel = detail?.galleryLabel || 'Portfolio';
  const galleryTitle = detail?.galleryTitle || 'Project Gallery';
  const heroHeight = detail?.heroHeight || service?.heroHeight || 70;
  const heroWidth = detail?.heroWidth || 100;

  return (
    <div className="sd">
      {/* ====== Hero Section with Video ====== */}
      <section className="sd__hero" style={{ height: `${heroHeight}vh`, width: heroWidth < 100 ? `${heroWidth}%` : '100%', margin: heroWidth < 100 ? '0 auto' : undefined }}>
        {videoUrl && (
          <div className="sd__hero-video-wrap">
            <video
              ref={videoRef}
              className="sd__hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              src={videoUrl.startsWith('http') ? videoUrl : `${API_URL}${videoUrl}`}
            />
            <div className="sd__hero-overlay" />
          </div>
        )}
        {!videoUrl && <div className="sd__hero-fallback" />}

        <div className="sd__hero-content">
          <motion.span
            className="sd__hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t('sd.ourServices')}
          </motion.span>
          <motion.h1
            className="sd__hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {title}
          </motion.h1>
          <motion.div
            className="divider"
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          />
        </div>
      </section>

      {/* ====== Description Section ====== */}
      <section className="sd__desc-section">
        <div className="container container--narrow">
          <motion.div
            className="sd__desc"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </section>

      {/* ====== Gallery Section ====== */}
      {gallery.length > 0 && (
        <section className="sd__gallery-section">
          <div className="container">
            <motion.div
              className="section__header"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="section__label">{galleryLabel}</span>
              <h2 className="section__title">{galleryTitle}</h2>
              <div className="divider" />
            </motion.div>

            <div className="sd__gallery-grid">
              {gallery.map((img, idx) => (
                <motion.div
                  key={idx}
                  className="sd__gallery-item"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * idx }}
                  onClick={() => setLightbox({ open: true, index: idx })}
                >
                  <img
                    src={`${API_URL}${img}`}
                    alt={`${title} project ${idx + 1}`}
                    loading="lazy"
                  />
                  <div className="sd__gallery-item-overlay">
                    <span className="sd__gallery-zoom-icon">&#x2922;</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ====== Back + CTA Section ====== */}
      <section className="sd__cta-section">
        <div className="container" style={{ textAlign: 'center' }}>
          <motion.h2
            className="sd__cta-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {t('sd.interested')}
          </motion.h2>
          <motion.p
            className="sd__cta-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t('sd.discuss')}
          </motion.p>
          <motion.div
            className="sd__cta-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <button className="btn btn--primary btn--lg" onClick={() => navigate('/contact')}>
              {t('nav.contact')}
            </button>
            <button className="btn btn--outline" onClick={() => navigate('/')}>
              &#8592; {t('sd.backHome')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* ====== Lightbox ====== */}
      <AnimatePresence>
        {lightbox.open && gallery.length > 0 && (
          <motion.div
            className="sd__lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox({ open: false, index: 0 })}
          >
            <button
              className="sd__lightbox-close"
              onClick={() => setLightbox({ open: false, index: 0 })}
            >
              &times;
            </button>

            <button
              className="sd__lightbox-arrow sd__lightbox-arrow--prev"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(prev => ({ ...prev, index: (prev.index - 1 + gallery.length) % gallery.length }));
              }}
            >
              &#8249;
            </button>

            <motion.img
              key={lightbox.index}
              className="sd__lightbox-img"
              src={`${API_URL}${gallery[lightbox.index]}`}
              alt={`${title} ${lightbox.index + 1}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            />

            <button
              className="sd__lightbox-arrow sd__lightbox-arrow--next"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(prev => ({ ...prev, index: (prev.index + 1) % gallery.length }));
              }}
            >
              &#8250;
            </button>

            <div className="sd__lightbox-counter">
              {lightbox.index + 1} / {gallery.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ServiceDetail;
